// Silent access-token refresh. Attached to the default axios instance, so
// it covers every existing call site (35+ across the app) that imports
// `axios` directly and builds its own Authorization header — no per-call
// refactor needed.
import axios from 'axios';
import store from '../store';
import { setAccessToken, logout } from '../slices/userSlice';

let isRefreshing = false;
let pendingQueue = [];

const processQueue = (error, token) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

const isAuthEndpoint = (url = '') =>
  url.includes('/api/users/refresh') || url.includes('/api/users/login');

const installAuthRefreshInterceptor = () => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;
      const message = error.response?.data?.message;
      const url = originalRequest?.url || '';

      // Not a 401, no request to retry, already retried once, or it's the
      // refresh/login call itself failing — nothing to silently recover.
      if (status !== 401 || !originalRequest || originalRequest._retry || isAuthEndpoint(url)) {
        return Promise.reject(error);
      }

      // A step-up-gated route returns 401 too, but refreshing the access
      // token can't fix a missing/invalid x-step-up-token — retrying would
      // just 401 again. Let it surface immediately instead of wasting a
      // refresh round-trip.
      if (message === 'Step-up verification required') {
        return Promise.reject(error);
      }

      // Guests have no refresh token to rotate — a 401 for a guest session
      // is a real auth failure, not an expired access token.
      const userInfo = store.getState()?.user?.userInfo;
      if (!userInfo || userInfo.isGuest) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest._retry = true;
          originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post('/api/users/refresh', {}, { withCredentials: true });
        store.dispatch(setAccessToken(data.token));
        processQueue(null, data.token);
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${data.token}` };
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(logout());
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

export default installAuthRefreshInterceptor;
